import * as Sentry from '@sentry/react';

// Error reporting + Session Replay for the public site. Initialized from
// its own module, imported FIRST in index.js, so Sentry is listening
// before any app code runs and can catch failures during initial render.
//
// Why this exists: until 2026-09-07 none of the three KBT apps had any
// client-side error reporting. The guest High Holiday seat checkout was
// broken for ten days — every attempt rejected, 8 people turned away —
// and nothing surfaced it, because a failed fetch in the browser leaves
// no trace anywhere we look. Backend faults land in Cloud Logging;
// browser faults landed nowhere.
//
// Session Replay is on in production (DSN present) so we can watch the
// clicks and screens that led to a failure. Default Sentry masking is
// off (maskAllText / maskAllInputs / blockAllMedia) so replays show the
// real UI. Stripe iframes and password fields stay blocked/ignored —
// card data never leaves Stripe's iframe, and we do not record secrets.
// Tracing and profiling stay off — they mostly consume quota.
const dsn = process.env.REACT_APP_SENTRY_DSN;

// No DSN in local dev (.env sets none), so `yarn start` never reports.
// Guarding on the DSN rather than NODE_ENV also means a preview build can
// opt in just by setting the variable.
if (dsn) {
  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,

    // Never attach IPs, cookies or request bodies to an event. This is a
    // shul's members' data; an error report should say what broke, not
    // who was using it.
    sendDefaultPii: false,

    // Browser noise that is never actionable and would otherwise eat the
    // free-tier quota. ResizeObserver warnings are benign layout timing;
    // the rest are browser extensions and offline blips, not our code.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'top.GLOBALS',
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],

    // Low-traffic site: record every session. Drop replaysSessionSampleRate
    // (keep replaysOnErrorSampleRate at 1) if quota gets tight.
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        // https://docs.sentry.io/platforms/javascript/session-replay/privacy/#privacy-configuration
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
        block: [
          'iframe[srcdoc]',
          'iframe[src*="stripe"]',
          'iframe[src*="js.stripe.com"]',
          '.StripeElement',
        ],
        ignore: ['input[type="password"]'],
      }),
    ],
  });
}

export default Sentry;
